import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { UrlParam } from '@shared/enums/url-param.enum';
import { OrganizationInterface } from '@shared/interfaces/organization.interface';
import {
  EmailInfoInterface,
  MoodboardCategoryItem,
  MoodboardImageItem,
  PeopleInterface,
  PersonOrganisation,
  SocialMedia,
} from '@shared/interfaces/people.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { StringUtils } from '@shared/utils/string-utils';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PeopleHttpService extends BaseHttpService<PeopleInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  getApiRootPath(): string {
    return 'people';
  }

  read(id: number): Observable<PeopleInterface> {
    if (id) {
      let url = `${this.apiUrl}/${id}`;
      const encryptedCode = StringUtils.getParamFromUrl(UrlParam.Code);
      if (encryptedCode) {
        const encodeParam = StringUtils.replaceAll(encryptedCode, ' ', '%2B');
        url = `${url}?${UrlParam.Code}=${encodeParam}`;
        // clear param
        history.pushState(null, '', location.pathname);
      }
      return this.http
        .get<PeopleInterface>(url)
        .pipe(map(item => this.tranformData(item)));
    } else {
      return of(null);
    }
  }

  getMissingAttributes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/me/missing-attributes`);
  }

  paginate(
    params: HttpParams = null,
    innerEntity = null
  ): Observable<ApiGetResponse<PeopleInterface>> {
    return super.paginate(params, innerEntity).pipe(
      map((res: ApiGetResponse<PeopleInterface>) => {
        res.items = res.items.map((person: PeopleInterface) =>
          this.tranformPersonData(person)
        );
        return res;
      })
    );
  }

  paginateX(
    params: any,
    innerEntity = null
  ): Observable<ApiGetResponse<PeopleInterface>> {
    return super.paginateX(params, innerEntity).pipe(
      map((res: ApiGetResponse<PeopleInterface>) => {
        res.items = res.items.map((person: PeopleInterface) =>
          this.tranformPersonData(person)
        );
        return res;
      })
    );
  }

  filter(params: any): Observable<ApiGetResponse<PeopleInterface>> {
    const url = `${this.apiUrl}/mention`;

    return this.http
      .get<ApiGetResponse<PeopleInterface>>(url, {
        params,
      })
      .pipe(
        map((res: ApiGetResponse<PeopleInterface>) => {
          res.items = res.items.map((person: PeopleInterface) =>
            this.tranformPersonData(person)
          );

          return res;
        })
      );
  }

  private tranformPersonData(person: PeopleInterface): PeopleInterface {
    if (person) {
      const { firstName, lastName } = person;

      if (firstName && lastName) {
        person.name = `${firstName} ${lastName}`;
      } else if (firstName) {
        person.name = firstName;
      } else if (lastName) {
        person.lastName = lastName;
      }
    }
    return person;
  }

  getAfferableOrgs(
    entityId: number,
    params = null
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    const url = `${this.apiUrl}/${entityId}/affable-orgs`;
    params ??= new HttpParams();

    return this.http.get<ApiGetResponse<OrganizationInterface>>(url, {
      params,
    });
  }

  getOtherOrgs(
    id: number,
    params: HttpParams = new HttpParams()
  ): Observable<ApiGetResponse<PersonOrganisation>> {
    const url = `${this.apiUrl}/${id}/orgs`;

    return this.http.get<ApiGetResponse<PersonOrganisation>>(url, {
      params,
    });
  }

  // Batch add/remove: POST/DELETE /people/{id}/orgs take a raw array of org ids.
  addOrgs(id: number, organizationIds: number[]): Observable<unknown> {
    const url = `${this.apiUrl}/${id}/orgs`;
    return this.http.post(url, organizationIds);
  }

  removeOrgs(id: number, organizationIds: number[]): Observable<unknown> {
    const url = `${this.apiUrl}/${id}/orgs`;
    return this.http.delete(url, { body: organizationIds });
  }

  gettMoodboardCategories(section): Observable<MoodboardCategoryItem[]> {
    const url = `${this.apiUrl}/categories`;
    const params = new HttpParams().set('section', section);
    return this.http.get<MoodboardCategoryItem[]>(url, { params });
  }

  getMoodboardImages(category): Observable<MoodboardImageItem[]> {
    const url = `${this.apiUrl}/moodboard-images`;
    const params = new HttpParams().set('category', category);
    return this.http.get<MoodboardImageItem[]>(url, { params });
  }

  getEmailInfo(email: string): Observable<EmailInfoInterface> {
    const url = `${this.apiUrl}/email-info`;
    const params = new HttpParams().set('email', email);

    return this.http.get<EmailInfoInterface>(url, { params });
  }

  getVCard(id: number, size = 5): Observable<string> {
    if (!id) return of('');

    const url = `${this.apiUrl}/${id}/vcard`;
    const params = new HttpParams().set('size', size + '');
    return this.http.get<string>(url, { params });
  }

  uploadFavicon(urls: string[]): Observable<SocialMedia> {
    return this.http.post(`${this.apiUrl}/socialmedia`, urls);
  }

  deactivateAccount(params) {
    const url = `${this.apiUrl}/deactivate`;
    return this.http.post(url, null, { params });
  }
}
